import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { format, startOfMonth } from 'date-fns';
import { LineChart as LineIcon, RefreshCw, Loader2, AlertCircle, ShieldAlert, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { K2Controls } from '../components/k2/K2Controls';
import { K2Kpis } from '../components/k2/K2Kpis';
import { K2Analysis } from '../components/k2/K2Analysis';
import { K2DocsSection, K2NesSection } from '../components/k2/K2Sections';
import { K2ItemsSection } from '../components/k2/K2ItemsSection';
import { K2NesMovesSection } from '../components/k2/K2NesMovesSection';
import { computeRange, fmtCz, summarize } from '../components/k2/k2utils';

const API = '/api';

const initial = {
  preset: 'this_week',
  custom: { from: format(startOfMonth(new Date()), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') },
  mode: 'compare',
  view: 'table',
  book: 'all',
  warehouse: 'all',
};

function useK2Data(range, mode) {
  const [state, setState] = useState({ moves: [], nes: { items: [], total_value: 0 }, transfers: [], loading: true, error: null, fetchedAt: null });
  const [it, setIt] = useState({ items: [], loading: true, error: null });
  const reqId = useRef(0);
  const from = mode === 'compare' ? range.prevFrom : range.from;
  const load = useCallback(async (refresh = false) => {
    const id = ++reqId.current;
    const params = { date_from: from, date_to: range.to, ...(refresh ? { refresh: true } : {}) };
    setState((s) => ({ ...s, loading: true, error: null }));
    setIt((s) => ({ ...s, loading: true, error: null }));
    const ranges = mode === 'compare' ? [[range.prevFrom, range.prevTo], [range.from, range.to]] : [[range.from, range.to]];
    Promise.all(ranges.map(([f, t]) => axios.get(`${API}/k2/items`, { params: { ...params, date_from: f, date_to: t }, withCredentials: true })))
      .then((rs) => id === reqId.current && setIt({ items: rs.flatMap((r) => r.data.items), loading: false, error: null }))
      .catch((e) => id === reqId.current && setIt((s) => ({ ...s, loading: false, error: e.response?.data?.detail || 'Nepodařilo se načíst položky z K2' })));
    try {
      const [m, n, t] = await Promise.all([
        axios.get(`${API}/k2/movements`, { params, withCredentials: true }),
        axios.get(`${API}/k2/nes-stock`, { params: refresh ? { refresh: true } : {}, withCredentials: true }),
        axios.get(`${API}/k2/nes-transfers`, { params, withCredentials: true }),
      ]);
      if (id === reqId.current) setState({ moves: m.data.items, nes: n.data, transfers: t.data.items, loading: false, error: null, fetchedAt: m.data.fetched_at });
    } catch (e) {
      if (id === reqId.current) setState((s) => ({ ...s, loading: false, error: e.response?.data?.detail || 'Nepodařilo se načíst data z K2' }));
    }
  }, [from, range.to, range.from, range.prevFrom, range.prevTo, mode]);
  useEffect(() => { load(); }, [load]);
  return { ...state, items: it, reload: () => load(true) };
}

export default function AnalyticsK2Page() {
  const { canViewOverviews } = useAuth();
  const [s, setS] = useState(initial);
  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  const range = useMemo(() => computeRange(s.preset, s.custom), [s.preset, s.custom]);
  const data = useK2Data(range, s.mode);

  const warehouses = useMemo(() => [...new Set(data.moves.map((m) => m.warehouse).filter(Boolean))].sort(), [data.moves]);
  const warehouse = warehouses.includes(s.warehouse) ? s.warehouse : 'all';
  const moves = useMemo(() => data.moves.filter((m) => (s.book === 'all' || m.book === s.book) && (warehouse === 'all' || m.warehouse === warehouse)), [data.moves, s.book, warehouse]);
  const nes = data.nes.items;
  const cur = summarize(moves, nes, range.from, range.to);
  const prev = summarize(moves, nes, range.prevFrom, range.prevTo);
  const docs = useMemo(() => moves.filter((m) => m.date >= range.from && m.date <= range.to), [moves, range]);
  const lineItems = useMemo(() => data.items.items.filter((m) => (s.book === 'all' || m.book === s.book) && (warehouse === 'all' || m.warehouse === warehouse)), [data.items.items, s.book, warehouse]);

  if (!canViewOverviews) {
    return (
      <Layout>
        <div className="py-20 text-center" data-testid="k2-no-access">
          <ShieldAlert className="w-14 h-14 text-[#E2E8F0] mx-auto mb-3" />
          <p className="text-[#57534E]">Nemáte oprávnění k zobrazení této analýzy.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5" data-testid="k2-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Link to="/analytika" className="inline-flex items-center gap-1 text-sm text-[#57534E] hover:text-[#16A34A] mb-1" data-testid="k2-back-link">
              <ArrowLeft className="w-4 h-4" /> Analytika
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] font-['Manrope'] flex items-center gap-3">
              <LineIcon className="w-8 h-8 text-[#16A34A]" /> Výdeje a Sklad NES
            </h1>
            <p className="text-[#57534E] mt-1" data-testid="k2-range-label">
              Období {fmtCz(range.from)} – {fmtCz(range.to)}
              {s.mode === 'compare' && <> · srovnání s {fmtCz(range.prevFrom)} – {fmtCz(range.prevTo)}</>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {data.fetchedAt && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-[#F0FDF4] text-[#166534]" data-testid="k2-fetched-at">
                Data z K2: {new Date(data.fetchedAt).toLocaleString('cs-CZ', { timeZone: 'Europe/Prague' })}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={data.reload} disabled={data.loading || data.items.loading} data-testid="k2-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${data.loading ? 'animate-spin' : ''}`} /> Obnovit
            </Button>
          </div>
        </div>

        <K2Controls s={{ ...s, warehouse }} set={set} warehouses={warehouses} />

        {data.error && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-[#FEE2E2] text-[#991B1B] text-sm" data-testid="k2-error">
            <AlertCircle className="w-4 h-4" /> {data.error}
          </div>
        )}

        {data.loading && !data.fetchedAt ? (
          <div className="py-20 flex justify-center" data-testid="k2-loading"><Loader2 className="w-8 h-8 animate-spin text-[#16A34A]" /></div>
        ) : (
          <div className={`space-y-5 transition-opacity ${data.loading ? 'opacity-50 pointer-events-none' : ''}`} data-testid="k2-content">
            <K2Kpis cur={cur} prev={prev} nesTotal={data.nes.total_value} showCompare={s.mode === 'compare'} />
            <div className={`bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-sm`} data-testid="k2-analysis">
              <K2Analysis mode={s.mode} view={s.view} moves={moves} nes={nes} range={range} cur={cur} prev={prev} />
              <p className="text-xs text-[#94A3B8] mt-3">Pohyby = výdejky řad 290 a 291 (skutečná skladová cena). Ztráty = položky ve skladu NES přiřazené k období podle data šarže.</p>
            </div>
            <K2ItemsSection items={lineItems} loading={data.items.loading} error={data.items.error} range={range} withPrev={s.mode === 'compare'} />
            <K2DocsSection docs={docs} items={data.items.items} itemsLoading={data.items.loading} />
            <K2NesMovesSection transfers={data.transfers} range={range} withPrev={s.mode === 'compare'} />
            <K2NesSection items={nes} />
          </div>
        )}
      </div>
    </Layout>
  );
}
